'use client';
import { useEffect, useRef, useState } from 'react';
import { markNoteShown, claimSlot, releaseSlot } from '@/lib/eveningClient';
import { useEvening } from '@/lib/useEvening';
import { logCtaImpression } from '@/lib/ctaTracking';

// Evening reassurance note (Founder 14/10/26): a short, calm paragraph beside the WhatsApp CTA after the evening window opens
// (Israel clock; lib/eveningClient.ts). Copy = the visitor's he_cta_variants 'evening_note' variant. The impression is logged once
// per view; the click is logged by logInquiry (lib/inquiries.ts), which also stamps he_inquiries.evening_variant_id.
// `unique`: in a list of pro cards only the first note renders (one note for the whole list, not one per card).
// module-level: one impression per page view even when a card-list instance remounts (claimSlot/releaseSlot) — Finding 2
const loggedImpr = new Set<string>();
export default function EveningNote({ articleId, unique, style }: { articleId?: number | null; unique?: string; style?: React.CSSProperties }) {
  const { variant } = useEvening();
  const [mine, setMine] = useState(!unique);
  useEffect(() => {
    if (!unique) return;
    const ok = claimSlot(unique); setMine(ok);
    return () => { if (ok) releaseSlot(unique); };
  }, [unique]);
  const ref = useRef<HTMLParagraphElement>(null);
  const [seen, setSeen] = useState(false);
  const vid = mine && variant ? variant.id : null;
  const logged = useRef<number | null>(null);
  // a note inside a CSS-hidden wrapper (e.g. the desktop-only card column at 390) is not "shown": no impression, no click attribution
  useEffect(() => { setSeen(!!vid && !!ref.current && ref.current.offsetParent !== null); }, [vid]);
  useEffect(() => {
    if (!vid || !seen) return;
    markNoteShown(vid);
    const key = `${vid}:${location.pathname}`;
    if (logged.current !== vid && !loggedImpr.has(key)) { logged.current = vid; loggedImpr.add(key); logCtaImpression(vid, articleId ?? undefined); }
    return () => markNoteShown(null);
  }, [vid, articleId, seen]);
  if (!vid || !variant) return null;
  return (
    <p ref={ref} data-evening-note={variant.id} role="note"
      style={{ margin: '8px 0 0', padding: '8px 12px', borderRadius: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontSize: 13, lineHeight: 1.55, textAlign: 'start', ...style }}>
      {variant.copyText}
    </p>
  );
}
